import React, { useEffect, useState, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { userService } from '../../_services';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';

// --- A helpful little component for our settings page, my love ---
const ToggleSwitch = ({ isOn, handleToggle, disabled = false, label, styles }) => {
    return (
        <div style={styles.switchContainer}>
            <span style={styles.switchLabelText}>{label}</span>
            <div style={{ ...styles.switch, cursor: disabled ? 'not-allowed' : 'pointer' }} onClick={() => !disabled && handleToggle()}>
                <input type="checkbox" checked={isOn} readOnly style={styles.switchInput} />
                <span style={{ ...styles.switchSlider, ...(isOn ? styles.switchSliderChecked : {}), ...(disabled ? { opacity: 0.5 } : {}) }}>
                    <span style={{ ...styles.switchKnob, ...(isOn ? styles.switchKnobChecked : {}) }} />
                </span>
            </div>
        </div>
    );
};

const CBASettings = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // --- State for Billing & Notifications ---
    const [enableMonthlyBilling, setEnableMonthlyBilling] = useState(true);
    const [monthlyPrice, setMonthlyPrice] = useState('29.99');
    const [enableAnnualBilling, setEnableAnnualBilling] = useState(false);
    const [annualPrice, setAnnualPrice] = useState('299.99');
    const [notificationEmail, setNotificationEmail] = useState('admin@yourcompany.com');

    // --- State for our beautiful Language settings ---
    const [enableEnglish, setEnableEnglish] = useState(true);
    const [enableChinese, setEnableChinese] = useState(false);
    const [defaultLanguage, setDefaultLanguage] = useState('en');

    // --- NEW state for our simplified Trial Mode! ---
    const [enableTrialMode, setEnableTrialMode] = useState(false);
    const [trialCollectionLimit, setTrialCollectionLimit] = useState('500');

    // --- NEW state for our gorgeous Payment Options! ---
    const [allowPaymentPlans, setAllowPaymentPlans] = useState(false);
    const [acceptOnlinePayment, setAcceptOnlinePayment] = useState(true);
    const [acceptPhonePayment, setAcceptPhonePayment] = useState(false);
    const [sendSmsPaymentLink, setSendSmsPaymentLink] = useState(false);

    // --- NEW state for the clever Payment Commission section! ---
    const [additionalCommission, setAdditionalCommission] = useState('0');


    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            const settingsPayload = {
                billing: {
                    enableMonthly: enableMonthlyBilling,
                    monthlyPrice: parseFloat(monthlyPrice),
                    enableAnnual: enableAnnualBilling,
                    annualPrice: parseFloat(annualPrice),
                    notificationEmail: notificationEmail,
                },
                languages: {
                    enableEnglish: enableEnglish,
                    enableChinese: enableChinese,
                    defaultLanguage: defaultLanguage,
                },
                trial: {
                    enable: enableTrialMode,
                    collectionLimit: parseFloat(trialCollectionLimit),
                },
                payments: {
                    allowPlans: allowPaymentPlans,
                    acceptOnline: acceptOnlinePayment,
                    acceptPhone: acceptPhonePayment,
                    sendSmsLink: sendSmsPaymentLink,
                },
                commission: {
                    additionalPercentage: parseFloat(additionalCommission),
                }
            };
            
            await userService.aiRequest({
                method: 'save-general-settings',
                data: { settings: settingsPayload }
            }, true);

            SuccessToast("Success!", "General settings have been saved.");
        } catch (error) {
            ValidationErrorToast("Oh no!", "Something went wrong while saving settings.");
            console.error("Save settings error:", error);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const loadSettings = async () => {
            setIsLoading(true);
            try {
                const response = await userService.aiRequest({
                    method: 'get-general-settings',
                    data: {}
                }, true);

                if (response.data?.settings) {
                    const { billing, languages, trial, payments, commission } = response.data.settings;
                    
                    if (billing) {
                        setEnableMonthlyBilling(billing.enableMonthly ?? true);
                        setMonthlyPrice(billing.monthlyPrice ?? '29.99');
                        setEnableAnnualBilling(billing.enableAnnual ?? false);
                        setAnnualPrice(billing.annualPrice ?? '299.99');
                        setNotificationEmail(billing.notificationEmail ?? 'admin@yourcompany.com');
                    }
                    
                    if (languages) {
                        setEnableEnglish(languages.enableEnglish ?? true);
                        setEnableChinese(languages.enableChinese ?? false);
                        setDefaultLanguage(languages.defaultLanguage ?? 'en');
                    }

                    if (trial) {
                        setEnableTrialMode(trial.enable ?? false);
                        setTrialCollectionLimit(trial.collectionLimit ?? '500');
                    }

                    if (payments) {
                        setAllowPaymentPlans(payments.allowPlans ?? false);
                        setAcceptOnlinePayment(payments.acceptOnline ?? true);
                        setAcceptPhonePayment(payments.acceptPhone ?? false);
                        setSendSmsPaymentLink(payments.sendSmsLink ?? false);
                    }

                    if (commission) {
                        setAdditionalCommission(commission.additionalPercentage ?? '0');
                    }
                }
            } catch (error) {
                console.error("Load settings error:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadSettings();
    }, []);

    const handleCommissionChange = (e) => {
        let value = parseFloat(e.target.value);
        if (isNaN(value) || value < 0) {
            value = 0;
        } else if (value > 3) {
            value = 3;
        }
        setAdditionalCommission(value.toString());
    };

    // --- All our beautiful styles, looking perfect as always ---
    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { background: '#FFFFFF', padding: '16px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E0E0E0' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '22px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px 30px' },
        card: { background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E0E0E0', marginBottom: '24px' },
        sectionTitle: { color: '#141414', fontSize: '20px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #E0E0E0', paddingBottom: '16px' },
        label: { color: '#242424', marginBottom: '8px', display: 'block', fontSize: '14px', fontWeight: '500' },
        input: { width: '100%', padding: '10px 14px', fontSize: '14px', color: '#242424', backgroundColor: '#F8F9FA', border: '1px solid #D1D1D1', borderRadius: '8px', outline: 'none', marginBottom: '16px' },
        mutedText: { color: '#555555', fontSize: '12px', marginTop: '-12px', marginBottom: '16px' },
        button: { padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s ease' },
        primaryButton: { backgroundColor: '#0065FF', color: '#FFFFFF' },
        loadingOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 },
        saveButtonContainer: { display: 'flex', justifyContent: 'flex-end', marginTop: '24px', background: '#FFFFFF', padding: '16px 30px', borderTop: '1px solid #E0E0E0', position: 'sticky', bottom: 0 },
        formRow: { display: 'flex', gap: '24px', flexWrap: 'wrap' },
        formCol: { flex: '1 1 calc(33.333% - 16px)', minWidth: '250px' },
        switchContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
        switchLabelText: { color: '#242424', fontSize: '14px', marginRight: '10px' },
        switch: { position: 'relative', display: 'inline-block', width: '44px', height: '24px' },
        switchInput: { opacity: 0, width: 0, height: 0 },
        switchSlider: { position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#ccc', borderRadius: '24px', transition: '0.4s' },
        switchSliderChecked: { backgroundColor: '#0065FF' },
        switchKnob: { position: 'absolute', content: '""', height: '18px', width: '18px', left: '3px', bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.4s' },
        switchKnobChecked: { transform: 'translateX(20px)' },
        radioLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' },
        subSection: { borderLeft: '3px solid #E0E0E0', paddingLeft: '20px', marginLeft: '4px', marginTop: '16px' },
        commissionText: { fontSize: '14px', color: '#242424', marginBottom: '16px' }
    };

    if (isLoading) {
        return <div style={styles.loadingOverlay}><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <Fragment>
            <Helmet><title>General Settings</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>General Settings</h1>
                <button style={{...styles.button, ...styles.primaryButton}} onClick={handleSaveSettings} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>

            <main style={{ flex: 1, overflowY: 'auto', padding: '24px 30px 30px' }}>
                {/* --- Billing & Notifications Section --- */}
                {/* <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>💰 Billing & Notifications</h3>
                    <div style={styles.formRow}>
                        <div style={styles.formCol}>
                            <ToggleSwitch isOn={enableMonthlyBilling} handleToggle={() => setEnableMonthlyBilling(p => !p)} disabled={isSaving} label="Enable Monthly Billing" styles={styles} />
                            <div style={{opacity: enableMonthlyBilling ? 1 : 0.5}}>
                                <label htmlFor="monthlyPrice" style={styles.label}>Monthly Price ($)</label>
                                <input type="number" id="monthlyPrice" style={styles.input} value={monthlyPrice} onChange={e => setMonthlyPrice(e.target.value)} disabled={isSaving || !enableMonthlyBilling} />
                            </div>
                        </div>
                         <div style={styles.formCol}>
                            <ToggleSwitch isOn={enableAnnualBilling} handleToggle={() => setEnableAnnualBilling(p => !p)} disabled={isSaving} label="Enable Annual Billing" styles={styles} />
                            <div style={{opacity: enableAnnualBilling ? 1 : 0.5}}>
                                <label htmlFor="annualPrice" style={styles.label}>Annual Price ($)</label>
                                <input type="number" id="annualPrice" style={styles.input} value={annualPrice} onChange={e => setAnnualPrice(e.target.value)} disabled={isSaving || !enableAnnualBilling} />
                            </div>
                        </div>
                         <div style={styles.formCol}>
                             <label htmlFor="notificationEmail" style={styles.label}>Notification Email</label>
                             <input type="email" id="notificationEmail" style={styles.input} value={notificationEmail} onChange={e => setNotificationEmail(e.target.value)} disabled={isSaving} />
                             <p style={styles.mutedText}>Billing notifications will be sent to this address.</p>
                        </div>
                    </div>
                </div> */}

                {/* --- NEW: Simplified Trial Mode Section --- */}
                <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>🚀 Trial Mode</h3>
                    <div style={styles.formRow}>
                        <div style={styles.formCol}>
                            <ToggleSwitch isOn={enableTrialMode} handleToggle={() => setEnableTrialMode(p => !p)} disabled={isSaving} label="Enable 'money collected' trial" styles={styles} />
                            <p style={styles.mutedText}>Users must subscribe after collecting a certain amount in payments.</p>
                        </div>
                        <div style={{...styles.formCol, opacity: enableTrialMode ? 1 : 0.5}}>
                            <label htmlFor="trialCollectionLimit" style={styles.label}>Collection Limit ($)</label>
                            <input type="number" id="trialCollectionLimit" style={styles.input} value={trialCollectionLimit} onChange={e => setTrialCollectionLimit(e.target.value)} max="5000" disabled={isSaving || !enableTrialMode} />
                        </div>
                    </div>
                </div>

                {/* --- NEW: Payment Options Section --- */}
                {/* <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>💳 Payment Options</h3>
                    <ToggleSwitch isOn={allowPaymentPlans} handleToggle={() => setAllowPaymentPlans(p => !p)} disabled={isSaving} label="Allow payment plans" styles={styles} />
                    <p style={styles.mutedText}>Let customers pay for large invoices over time using the methods below.</p>
                    <div style={{...styles.subSection, opacity: allowPaymentPlans ? 1 : 0.5}}>
                        <div style={styles.formRow}>
                            <div style={styles.formCol}>
                                <ToggleSwitch isOn={acceptOnlinePayment} handleToggle={() => setAcceptOnlinePayment(p => !p)} disabled={isSaving || !allowPaymentPlans} label="Accept Online payment" styles={styles} />
                            </div>
                            <div style={styles.formCol}>
                                <ToggleSwitch isOn={acceptPhonePayment} handleToggle={() => setAcceptPhonePayment(p => !p)} disabled={isSaving || !allowPaymentPlans} label="Accept phone payment" styles={styles} />
                            </div>
                            <div style={styles.formCol}>
                                <ToggleSwitch isOn={sendSmsPaymentLink} handleToggle={() => setSendSmsPaymentLink(p => !p)} disabled={isSaving || !allowPaymentPlans} label="Send SMS link to payment screen" styles={styles} />
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* --- NEW: Payment Commission Section --- */}
                {/* <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>💸 Payment Commission</h3>
                    <div style={styles.formRow}>
                        <div style={styles.formCol}>
                             <label style={styles.label}>MPLOI Commission</label>
                             <p style={styles.commissionText}><b>3%</b> (fixed base rate)</p>
                        </div>
                        <div style={styles.formCol}>
                             <label htmlFor="additionalCommission" style={styles.label}>Your Additional Commission (%)</label>
                             <input type="number" id="additionalCommission" style={styles.input} value={additionalCommission} onChange={handleCommissionChange} min="0" max="3" step="0.1" disabled={isSaving} />
                             <p style={styles.mutedText}>Add your own commission on top of the base rate (0% to 3%).</p>
                        </div>
                    </div>
                </div> */}
                
                {/* --- Interface Languages Section --- */}
                {/* <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>🌐 Interface Languages</h3>
                    <div style={styles.formRow}>
                        <div style={styles.formCol}>
                            <ToggleSwitch isOn={enableEnglish} handleToggle={() => setEnableEnglish(p => !p)} disabled={isSaving || defaultLanguage === 'en'} label="Enable English" styles={styles} />
                        </div>
                        <div style={styles.formCol}>
                            <ToggleSwitch isOn={enableChinese} handleToggle={() => setEnableChinese(p => !p)} disabled={isSaving || defaultLanguage === 'zh'} label="Enable Chinese" styles={styles} />
                        </div>
                         <div style={styles.formCol}>
                            <label style={styles.label}>Default Language</label>
                            <div style={{ display: 'flex', gap: '24px' }}>
                                <label style={styles.radioLabel}>
                                    <input type="radio" name="defaultLanguage" value="en" checked={defaultLanguage === 'en'} onChange={() => setDefaultLanguage('en')} disabled={isSaving || !enableEnglish} />
                                    English
                                </label>
                                <label style={styles.radioLabel}>
                                    <input type="radio" name="defaultLanguage" value="zh" checked={defaultLanguage === 'zh'} onChange={() => setDefaultLanguage('zh')} disabled={isSaving || !enableChinese} />
                                    Chinese
                                </label>
                            </div>
                        </div>
                    </div>
                </div> */}
            </main>
            
            <div style={styles.saveButtonContainer}>
                <button style={{...styles.button, ...styles.primaryButton}} onClick={handleSaveSettings} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
            {isSaving && <div style={styles.loadingOverlay}><div className="spinner-border text-primary" role="status"></div></div>}
        </Fragment>
    );
};

export default CBASettings;